import { auth, firebase } from '../config/firebase.config';

export interface FirebaseAuthenticationSession {
  uid: string;
  email?: string;
  idToken: string;
}

export interface FirebaseRefreshedSession {
  email?: string;
  idToken: string;
}

export class FirebaseVerificationCooldownError extends Error {
  constructor(public remainingSeconds: number) {
    super('VERIFICATION_COOLDOWN');
    this.name = 'FirebaseVerificationCooldownError';
  }
}

/**
 * Autentica con email/clave y entrega un ID Token verificable por el backend.
 */
export class EmailPasswordAuthenticationService {
  private static instance: EmailPasswordAuthenticationService;
  private persistenceConfigured = false;
  private lastVerificationSentAt = 0;
  private readonly verificationCooldownMs = 60000;

  private constructor() {}

  static getInstance(): EmailPasswordAuthenticationService {
    if (!EmailPasswordAuthenticationService.instance) {
      EmailPasswordAuthenticationService.instance = new EmailPasswordAuthenticationService();
    }

    return EmailPasswordAuthenticationService.instance;
  }

  async authenticate(email: string, password: string): Promise<FirebaseAuthenticationSession> {
    await this.configurePersistence();
    const normalizedEmail = email.trim().toLowerCase();
    const credential = await auth.signInWithEmailAndPassword(normalizedEmail, password);
    const firebaseUser = credential.user;

    if (!firebaseUser) {
      throw new Error('No se pudo obtener la cuenta autenticada.');
    }

    const idToken = await firebaseUser.getIdToken(true);
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || normalizedEmail,
      idToken
    };
  }

  async getCurrentIdToken(): Promise<string> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('La sesión Firebase no está disponible.');
    return currentUser.getIdToken(true);
  }

  private async configurePersistence(): Promise<void> {
    if (this.persistenceConfigured || typeof auth.setPersistence !== 'function') return;
    await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    this.persistenceConfigured = true;
  }

  async refreshCurrentUser(): Promise<FirebaseRefreshedSession> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('FIREBASE_SESSION_UNAVAILABLE');
    await currentUser.reload();
    const refreshedUser = auth.currentUser;
    if (!refreshedUser) throw new Error('FIREBASE_SESSION_UNAVAILABLE');

    return {
      email: refreshedUser.email || undefined,
      idToken: await refreshedUser.getIdToken(true)
    };
  }

  getVerificationCooldownSeconds(): number {
    const remaining = this.verificationCooldownMs - (Date.now() - this.lastVerificationSentAt);
    return Math.max(0, Math.ceil(remaining / 1000));
  }

  async sendEmailVerification(): Promise<void> {
    const remainingSeconds = this.getVerificationCooldownSeconds();
    if (remainingSeconds > 0) {
      throw new FirebaseVerificationCooldownError(remainingSeconds);
    }

    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('FIREBASE_SESSION_UNAVAILABLE');
    auth.languageCode = 'es';
    await currentUser.sendEmailVerification();
    this.lastVerificationSentAt = Date.now();
  }

  resetRuntimeState(): void {
    this.persistenceConfigured = false;
    this.lastVerificationSentAt = 0;
  }
}

export const emailPasswordAuthenticationService = EmailPasswordAuthenticationService.getInstance();
